// initSentry MUST be the first call — before any React tree or router is created
import { initSentry } from "./lib/sentry";
initSentry();

import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './redux/store';
import App from "./App";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import './index.scss';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { currentAppEnv } from './data/env-constants';

Sentry.logger.info("App bootstrapping", { env: currentAppEnv });

const fallbackComponent = () => {
  function handleReload() {
    window.location.reload();
  }
  return (
    <div className="h-[100vh] w-[100vw] flex justify-center items-center">
      <p>Failed to load. Please refresh.</p>
      <div
        className="rista-sprite blue-refresh-copy rista-new-sprite cursor-pointer scale-125 ml-4"
        onClick={handleReload}
      />
    </div>
  );
};

// React 19: root-level global error hooks
const container = document.getElementById("root")!;
const root = createRoot(container, {
  // Error thrown and NOT caught by any ErrorBoundary
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),

  // Error caught INSIDE an ErrorBoundary
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Caught error", error, errorInfo.componentStack);
  }),

  // Error React automatically recovers from
  onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Recoverable error", error, errorInfo.componentStack);
  }),
});

root.render(
  <Sentry.ErrorBoundary fallback={fallbackComponent()}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
      <ToastContainer />
    </Provider>
  </Sentry.ErrorBoundary>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
